// Home, signed in: a greeting and when the TUNE MFD last synced, what to do next, the cars at a glance, the latest
// datalogs, an admin's groups, and TUNE's bulletin board. A new owner sees how to get going first.
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { data } from '../../api';
import { useAuth } from '../../auth';
import { agoText, count, greeting, lastSynced, whenText } from '../../lib/words';
import { keys, latestDatalogs, useAccount, useVehicles } from '../../queries';
import { Page } from '../../shell/Page';
import { Badge, ButtonLink, Card, ErrorBanner, ListLink, ListSection, Skeleton, useNow } from '../../ui';
import { CheckIcon, CpuIcon, FileArrowUpIcon, GaugeIcon, PlusIcon, QrCodeIcon } from '../../ui/icons';
import { BulletinBoard } from '../bulletin/Board';
import { CarCards } from '../garage/CarCard';
import s from './Home.module.css';

type Vehicle = import('@tune/shared').components['schemas']['Vehicle'];

export function Home() {
  const auth = useAuth();
  const api = auth.api('owners');
  const session = auth.session('owners');
  const now = useNow(60_000);
  const account = useAccount();
  const devices = useQuery({
    queryKey: keys.devices,
    queryFn: () => data(api.GET('/devices')),
    refetchInterval: 60_000,
  });
  const vehicles = useVehicles();
  const bulletin = useQuery({ queryKey: keys.bulletin, queryFn: () => data(api.GET('/bulletin')) });
  const cars = vehicles.data?.vehicles ?? [];
  const logs = useQueries({ queries: cars.map((c) => latestDatalogs(api, c.id)) });
  const admin = account.data?.role === 'admin';
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => data(api.GET('/groups')),
    enabled: admin,
  });

  const first = (account.data?.displayName || session?.name || '').trim().split(/\s+/)[0] ?? '';
  const synced = lastSynced(devices.data?.devices ?? []);
  const mfds = (devices.data?.devices ?? []).filter((d) => d.kind === 'mfd').length;
  const latest = logs
    .flatMap((q, i) => (q.data?.datalogs ?? []).map((log) => ({ log, car: cars[i] })))
    .filter((x): x is { log: (typeof x)['log']; car: Vehicle } => x.car !== undefined)
    .sort((a, b) => (b.log.startedAt ?? b.log.receivedAt).localeCompare(a.log.startedAt ?? a.log.receivedAt))
    .slice(0, 5);
  const posted =
    bulletin.data && (['news', 'releases', 'development'] as const).some((c) => bulletin.data[c].length > 0);
  const newOwner = !vehicles.isPending && !devices.isPending && (cars.length === 0 || mfds === 0);

  const sync = devices.isPending ? (
    'Checking your MFDs…'
  ) : synced ? (
    <span className={s.sync}>
      Your TUNE MFD synced <time dateTime={synced}>{agoText(synced, now)}</time>.
    </span>
  ) : (
    'Nothing has synced from a TUNE MFD yet.'
  );

  return (
    <Page title={`${greeting(new Date(now))}${first ? `, ${first}` : ''}`} tab="Home" root subtitle={sync}>
      {newOwner && (
        <Card title="Getting started" subtitle="Three steps, and your car's data comes to TUNE Analytics by itself.">
          <ol className={s.steps}>
            <Step
              done={cars.length > 0}
              title="Add your car"
              detail="Its name, and what it is."
              to="/garage?sheet=add-car"
              action="Add a car"
            />
            <Step
              done={mfds > 0}
              title="Sign in your TUNE MFD"
              detail="On the MFD, open Settings, then TUNE account, and scan its code with your phone's camera."
              to="/pair"
              action="Sign in an MFD"
            />
            <Step
              done={latest.length > 0}
              title="Go for a drive"
              detail="The MFD sends its datalogs once it's on Wi-Fi, and they're here to analyse."
            />
          </ol>
        </Card>
      )}

      <section className={s.section} aria-labelledby="quick">
        <h2 id="quick" className="visually-hidden">
          Quick actions
        </h2>
        <div className={s.quick}>
          <Link to="/pair" className={s.action}>
            <QrCodeIcon size={22} weight="bold" aria-hidden="true" />
            Sign in an MFD
          </Link>
          <Link to="/garage?sheet=add-car" className={s.action}>
            <PlusIcon size={22} weight="bold" aria-hidden="true" />
            Add a car
          </Link>
          <Link to="/telemetry/file" className={s.action}>
            <FileArrowUpIcon size={22} weight="bold" aria-hidden="true" />
            Open a datalog
          </Link>
          <Link to="/create/configs/new" className={s.action}>
            <CpuIcon size={22} weight="bold" aria-hidden="true" />
            New config
          </Link>
        </div>
      </section>

      {vehicles.isError && <ErrorBanner retry={() => void vehicles.refetch()}>Your cars couldn't load.</ErrorBanner>}
      {vehicles.isPending && <Skeleton blocks={2} lines={0} label="Loading your cars" />}
      {cars.length > 0 && (
        <section className={s.section} aria-labelledby="home-cars">
          <div className={s.sectionHead}>
            <h2 id="home-cars" className={s.sectionTitle}>
              Your cars
            </h2>
            <ButtonLink to="/garage" variant="plain" size="small">
              Garage
            </ButtonLink>
          </div>
          <CarCards cars={cars} now={now} />
        </section>
      )}

      {latest.length > 0 && (
        <ListSection
          header="Latest datalogs"
          headerAction={
            <ButtonLink to="/telemetry" variant="plain" size="small">
              Telemetry
            </ButtonLink>
          }
        >
          {latest.map(({ log, car }) => (
            <ListLink
              key={log.id}
              to={`/telemetry/${car.id}/${log.id}`}
              label={car.nickname}
              subtitle={whenText(log.startedAt ?? log.receivedAt, now)}
            />
          ))}
        </ListSection>
      )}

      {admin && (
        <Card
          title="You're an admin"
          subtitle="You invite people to TUNE Analytics, and make groups whose members get what TUNE gives them."
          actions={<Badge tone="accent">Admin</Badge>}
        >
          <div className={s.sectionHead}>
            <span>{groups.data ? count(groups.data.groups.length, 'group') : 'Your groups'}</span>
            <ButtonLink to="/admin" size="small" icon={<GaugeIcon size={16} weight="bold" />}>
              Admin dashboard
            </ButtonLink>
          </div>
        </Card>
      )}

      {posted && bulletin.data && <BulletinBoard board={bulletin.data} />}
    </Page>
  );
}

function Step({
  done,
  title,
  detail,
  to,
  action,
}: {
  done: boolean;
  title: string;
  detail: string;
  to?: string;
  action?: string;
}) {
  return (
    <li className={`${s.step} ${done ? s.stepDone : ''}`}>
      <span className={s.stepMark} aria-hidden="true">
        {done && <CheckIcon size={14} weight="bold" />}
      </span>
      <span className={s.stepText}>
        <span className={s.stepTitle}>
          {title}
          <span className="visually-hidden">{done ? ': done' : ''}</span>
        </span>
        <span className={s.stepDetail}>{detail}</span>
        {!done && to && action && (
          <span>
            <ButtonLink to={to} size="small" variant="tinted">
              {action}
            </ButtonLink>
          </span>
        )}
      </span>
    </li>
  );
}
